import { useEffect, useMemo } from "react";
import SpotSuccessLight from "@utilitywarehouse/hearth-svg-assets/lib/spot-success-light.svg";
import {
  BodyText,
  Card,
  CardContent,
  Flex,
  Heading,
  Link,
  Divider,
} from "@utilitywarehouse/hearth-react";
import { ChevronRightSmallIcon } from "@utilitywarehouse/hearth-react-icons";

import { useVulnerabilityContext } from "../../context/vulnerability/useVulnerabilityContext";
import QuestionsAnswersLayout from "../../components/QuestionsAnswersLayout";
import { mapPSRAnswersSummary } from "../utils";
import { MappedAnswers } from "../../context/vulnerability/VulnerabilityContext";
import { checkTokenExpiryOnInterval } from "../../context/auth/Token";
import { useAuthContext } from "../../context/auth/useAuthContext";
import Unauthenticated from "../../components/Unauthenticated";

import { CustomerPSRSubmissionDataTest } from "./constants";

const CustomerPSRSubmission = () => {
  const { answers } = useVulnerabilityContext();
  const { setAuthenticated, authenticated } = useAuthContext();

  const mappedAnswers = useMemo((): MappedAnswers => {
    return mapPSRAnswersSummary(answers);
  }, [answers]);

  useEffect(() => {
    checkTokenExpiryOnInterval(authenticated, setAuthenticated);
  }, []);

  return (
    <Flex direction="column" marginTop="150">
      {!authenticated ? (
        <Unauthenticated />
      ) : (
        <Flex direction="column" spacing="lg">
          <Flex direction="column" spacing="lg">
            <Card variant="subtle">
              <CardContent spacing="lg">
                <img
                  src={SpotSuccessLight}
                  style={{
                    width: "120px",
                    height: "160px",
                  }}
                  alt="Notebook success"
                />

                <Flex direction="column" spacing="lg" padding="400">
                  <Heading size="lg">You're all done </Heading>
                  <BodyText size="md">Thank you for your answers</BodyText>
                </Flex>
              </CardContent>
            </Card>
            {answers.psr?.psrOutcome.optedIn ? (
              <Flex
                direction="column"
                gap="175"
                data-testid={
                  CustomerPSRSubmissionDataTest.SUMMARY_CONTAINER_OPTED_IN
                }
              >
                {answers.psr?.psrOutcome.eligible ? (
                  <>
                    <BodyText>
                      We really appreciate the time you’ve taken to fill out
                      this survey, and we hope that we're able to make your life
                      that little bit easier using the answers you’ve provided.
                    </BodyText>
                    <BodyText>
                      We’ve saved this information on your account so that our
                      support staff have the most up-to-date information. They
                      may also ask you about it from time to time, if
                      appropriate.
                    </BodyText>
                    <BodyText>
                      You will receive a letter in the post confirming your
                      place on the Priority Service Register.
                    </BodyText>

                    <Divider />

                    <Flex direction="column" gap="250">
                      <Heading size="md">Other services we offer</Heading>
                      <BodyText>
                        We offer customers on the Priority Services Register
                        other services that require a phone call to arrange. For
                        example, you can add a trusted person to your account,
                        arrange meter repositioning, or qualify for an annual
                        Gas Safety check.
                      </BodyText>
                      <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://uw.co.uk/help/extra-help"
                      >
                        Find out more
                        <ChevronRightSmallIcon />
                      </Link>
                    </Flex>
                  </>
                ) : (
                  <>
                    <BodyText
                      data-testid={
                        CustomerPSRSubmissionDataTest.SUMMARY_CONTAINER_NOT_ELIGIBLE
                      }
                      weight="bold"
                    >
                      Based on the additional information you’ve provided you
                      are not eligible for the PSR
                    </BodyText>
                    <BodyText>
                      We’ve saved this information on your account so that our
                      support staff have the most up-to-date information. They
                      may also ask you about it from time to time, if
                      appropriate.
                    </BodyText>
                  </>
                )}
              </Flex>
            ) : (
              <Flex
                direction="column"
                gap="300"
                data-testid={
                  CustomerPSRSubmissionDataTest.SUMMARY_CONTAINER_OPTED_OUT
                }
              >
                <BodyText>
                  We’ve saved this information on your account so that our
                  support staff have the most up-to-date information. They may
                  also ask you about it from time to time.
                </BodyText>
                <QuestionsAnswersLayout mappedAnswers={mappedAnswers} />
                <BodyText>
                  You’ve chosen not to be added onto the Priority services
                  register. If your circumstances change this will still be
                  available to you in the future.
                </BodyText>
              </Flex>
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default CustomerPSRSubmission;
